import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { Crosshair } from "lucide-react";
import { Arma, backendOrigin } from "@/lib/armaApi";
import { useInstance } from "@/context/InstanceContext";
import CornerFrame from "@/components/CornerFrame";
import HeroBanner from "@/components/HeroBanner";

const DEFAULT_BOUNDS = [[0, 0], [12800, 12800]];

const SOURCES = [
  { id: "positions", label: "OPERATOR POSITIONS" },
  { id: "kills_victim", label: "KIA LOCATIONS (VICTIM)" },
  { id: "kills_killer", label: "FIRING POSITIONS (KILLER)" },
  { id: "ai_kills", label: "AI KIA LOCATIONS" },
  { id: "vehicles", label: "VEHICLE DESTRUCTION" },
];
const RANGES = [
  { id: "today", label: "TODAY" },
  { id: "7d", label: "7D" },
  { id: "all", label: "ALL" },
];
const FACTIONS = ["all", "US", "USSR", "FIA"];

function gridFallbackUrl(bounds = DEFAULT_BOUNDS) {
  const [[zMin, xMin], [zMax, xMax]] = bounds;
  const w = xMax - xMin, h = zMax - zMin;
  const step = 800;
  const lines = [];
  for (let i = 0; i <= Math.max(w, h); i += step) {
    lines.push(`<line x1='${i}' y1='0' x2='${i}' y2='${h}' stroke='%232e3725' stroke-width='6'/>`);
    lines.push(`<line x1='0' y1='${i}' x2='${w}' y2='${i}' stroke='%232e3725' stroke-width='6'/>`);
  }
  return `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${w} ${h}'><rect width='100%25' height='100%25' fill='%2311140f'/>${lines.join("")}<rect x='0' y='0' width='${w}' height='${h}' fill='none' stroke='%23b08d57' stroke-width='10'/></svg>`;
}

export default function Heatmap() {
  const { instance, mapInfo } = useInstance();
  const [source, setSource] = useState("positions");
  const [range, setRange] = useState("all");
  const [faction, setFaction] = useState("all");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const overlayRef = useRef(null);
  const heatRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const activeBounds = mapInfo?.bounds || DEFAULT_BOUNDS;
    const map = L.map(mapEl.current, {
      crs: L.CRS.Simple, minZoom: -3, maxZoom: 3, zoomSnap: 0.25,
      attributionControl: false, preferCanvas: true,
    });
    map.fitBounds(activeBounds);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (overlayRef.current) { overlayRef.current.remove(); overlayRef.current = null; }
    const activeBounds = mapInfo?.bounds || DEFAULT_BOUNDS;
    const url = mapInfo?.imageUrl ? `${backendOrigin()}${mapInfo.imageUrl}` : gridFallbackUrl(activeBounds);
    overlayRef.current = L.imageOverlay(url, activeBounds, { opacity: 1 }).addTo(map);
    map.fitBounds(activeBounds);
  }, [mapInfo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Arma.heatmap({ source, instance, range, faction })
      .then((d) => {
        if (cancelled) return;
        setCount(d.count);
        if (heatRef.current) { heatRef.current.remove(); heatRef.current = null; }
        if (d.points?.length) {
          heatRef.current = L.heatLayer(d.points, {
            radius: 28, blur: 22, maxZoom: 2, minOpacity: 0.4,
            gradient: { 0.2: "#3d4527", 0.4: "#7d9947", 0.6: "#d4a574", 0.8: "#e0a124", 1.0: "#dc2626" },
          }).addTo(map);
        }
      })
      .catch((e) => !cancelled && setError(e?.message || "Heatmap-Fehler"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [source, instance, range, faction, mapInfo?.mapName]);

  return (
    <div className="space-y-4 relative z-[2]">
      <HeroBanner
        eyebrow="AREA OF OPERATIONS · TACTICAL OVERLAY"
        title="HEATMAP"
        meta={
          <>
            AO: <span style={{ color: "var(--text)" }}>{mapInfo?.mapName || "—"}</span>
            <span className="mx-2">|</span>
            DATAPOINTS: <span style={{ color: "var(--amber-bright)" }}>{count.toLocaleString("de-DE")}</span>
            <span className="mx-2">|</span>
            GRID 0–12800 · CRS.SIMPLE
          </>
        }
        right={
          <div className="flex flex-wrap items-center gap-2 max-w-[460px] justify-end">
            <select value={source} onChange={(e) => setSource(e.target.value)}
              className="bg-[var(--bg)] border border-[var(--border-bright)] text-[var(--text)] text-xs px-3 py-2 font-mono-tech focus:outline-none focus:border-[var(--tan-bright)] uppercase tracking-wider">
              {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <select value={faction} onChange={(e) => setFaction(e.target.value)}
              className="bg-[var(--bg)] border border-[var(--border-bright)] text-[var(--text)] text-xs px-3 py-2 font-mono-tech focus:outline-none focus:border-[var(--tan-bright)] uppercase tracking-wider">
              {FACTIONS.map((f) => <option key={f} value={f}>{f === "all" ? "ALL FACTIONS" : f}</option>)}
            </select>
            <div className="flex border" style={{ borderColor: "var(--border-bright)" }}>
              {RANGES.map((r) => (
                <button key={r.id} onClick={() => setRange(r.id)}
                  className="px-3 py-2 text-xs font-display tracking-widest border-r last:border-r-0"
                  style={{
                    background: range === r.id ? "var(--tan-bright)" : "transparent",
                    color: range === r.id ? "#07060a" : "var(--text-muted)",
                    borderColor: "var(--border-bright)",
                  }}>{r.label}</button>
              ))}
            </div>
          </div>
        }
      />

      {error && (
        <div className="tac-panel p-3 text-xs font-mono-tech" style={{ borderColor: "var(--red)", color: "var(--red-bright)" }}>
          {error}
        </div>
      )}

      <CornerFrame className="tac-panel">
        <div className="tac-panel-header">
          <div className="label-tech flex items-center gap-2" style={{ color: "var(--tan)" }}>
            <Crosshair className="w-3 h-3" />
            GRID 0–12800 · CRS.SIMPLE · [Z,X]
          </div>
          <div className="label-tech" style={{ color: "var(--text-muted)" }}>{loading ? "PROCESSING …" : "READY"}</div>
        </div>
        <div className="relative" style={{ height: "75vh", minHeight: 540, background: "#0f1410" }}>
          <div ref={mapEl} className="absolute inset-0" />
          {/* Compass / N indicator */}
          <div className="absolute top-3 right-3 z-[450] pointer-events-none">
            <div className="px-2 py-1 text-[10px] font-mono-tech border" style={{
              background: "rgba(10,13,10,0.75)", borderColor: "var(--border-bright)", color: "var(--tan)",
            }}>▲ N</div>
          </div>
          {/* Legend */}
          <div className="absolute bottom-3 right-3 z-[450] pointer-events-none px-3 py-2 border" style={{
            background: "rgba(10,13,10,0.8)", borderColor: "var(--border-bright)",
          }}>
            <div className="label-tech mb-1" style={{ color: "var(--tan)" }}>INTENSITY</div>
            <div className="flex items-center gap-1.5">
              <span className="w-16 h-2" style={{ background: "linear-gradient(90deg, #3d4527, #7d9947, #d4a574, #e0a124, #dc2626)" }} />
              <span className="text-[9px] font-mono-tech" style={{ color: "var(--text-muted)" }}>LOW → HIGH</span>
            </div>
          </div>
          {(!mapInfo?.imageUrl) && (
            <div className="absolute bottom-3 left-3 z-[450] pointer-events-none px-3 py-1.5 border text-[10px] font-mono-tech" style={{
              background: "rgba(10,13,10,0.8)", borderColor: "var(--amber)", color: "var(--amber-bright)",
            }}>
              {`!! FALLBACK GRID – missing /maps/${mapInfo?.mapName || "?"}.png`}
            </div>
          )}
        </div>
      </CornerFrame>
    </div>
  );
}
